import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Plus, Trash2, Power, Mail, Shield, AlertTriangle, Code } from 'lucide-react';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal from '../components/Modal';
import Badge from '../components/Badge';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAdmin } from '../contexts/AdminContext';
import { formatDate } from '../utils/helpers';
import toast from 'react-hot-toast';

// HARDCODED: Only this domain is allowed for LBA super account emails
// This must match the backend constant in adminConvex.js
const LBA_ADMIN_EMAIL_DOMAIN = 'lba.me';

export default function LBASettings() {
    const { admin, isSuperAdmin } = useAdmin();
    const [showAddModal, setShowAddModal] = useState(false);
    const [newEmail, setNewEmail] = useState('');

    // Fetch all authorized emails
    const authorizedEmails = useQuery(api.superAccountEmails.getAllEmails);

    // Mutations
    const addEmail = useMutation(api.superAccountEmails.addAuthorizedEmail);
    const removeEmail = useMutation(api.superAccountEmails.removeAuthorizedEmail);
    const toggleEmail = useMutation(api.superAccountEmails.toggleEmailStatus);

    // Fetch LBA activity logs
    const activityLogs = useQuery(api.lbaActivityLogs.getRecentLogs, { limit: 20 });
    const activityStats = useQuery(api.lbaActivityLogs.getActivityStats, { days: 30 });

    // Validate email domain
    const isValidLBADomain = (email) => {
        if (!email) return false;
        const domain = email.toLowerCase().trim().split('@')[1];
        return domain === LBA_ADMIN_EMAIL_DOMAIN;
    };

    const handleAddEmail = async () => {
        if (!newEmail.trim()) return;

        // Client-side validation (server also validates)
        if (!isValidLBADomain(newEmail)) {
            toast.error(`Only @${LBA_ADMIN_EMAIL_DOMAIN} emails are allowed`);
            return;
        }

        try {
            await addEmail({
                email: newEmail.trim(),
                addedByAdminId: admin._id,
            });
            toast.success('Email added successfully');
            setNewEmail('');
            setShowAddModal(false);
        } catch (error) {
            toast.error(error.message || 'Failed to add email');
        }
    };

    const handleRemoveEmail = async (emailId) => {
        if (!confirm('Are you sure you want to remove this email?')) return;

        try {
            await removeEmail({
                emailId,
                removedByAdminId: admin._id,
            });
            toast.success('Email removed');
        } catch (error) {
            toast.error(error.message || 'Failed to remove email');
        }
    };

    const handleToggleEmail = async (emailId, currentStatus) => {
        try {
            await toggleEmail({
                emailId,
                isActive: !currentStatus,
                adminId: admin._id,
            });
            toast.success(currentStatus ? 'Email deactivated' : 'Email activated');
        } catch (error) {
            toast.error(error.message || 'Failed to update email status');
        }
    };

    const getActionTypeLabel = (actionType) => {
        switch (actionType) {
            case 'message_sent': return 'Message Sent';
            case 'ticket_created': return 'Ticket Created';
            case 'ticket_updated': return 'Ticket Updated';
            case 'ticket_resolved': return 'Ticket Resolved';
            case 'conversation_started': return 'Conversation Started';
            default: return actionType;
        }
    };

    const getActionTypeBadgeVariant = (actionType) => {
        switch (actionType) {
            case 'message_sent': return 'info';
            case 'ticket_created': return 'warning';
            case 'ticket_resolved': return 'success';
            default: return 'primary';
        }
    };

    if (!isSuperAdmin()) {
        return (
            <div className="text-center py-12">
                <Shield size={48} className="mx-auto mb-4 text-muted-light dark:text-muted-dark" />
                <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
                <p className="text-muted-light dark:text-muted-dark">
                    Only super admins can access LBA settings.
                </p>
            </div>
        );
    }

    if (authorizedEmails === undefined) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="large" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold mb-2">LBA Account Settings</h1>
                <p className="text-muted-light dark:text-muted-dark">
                    Manage emails authorized to access the LBA super account and view activity logs
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Authorized Emails */}
                <div className="lg:col-span-2 space-y-6">
                    <Card
                        title="Authorized Emails"
                        icon={<Mail size={20} />}
                        action={
                            <Button size="small" onClick={() => setShowAddModal(true)}>
                                <Plus size={16} />
                                Add Email
                            </Button>
                        }
                    >
                        <p className="text-sm text-muted-light dark:text-muted-dark mb-3">
                            These email addresses can log in and act as the LBA super account.
                            All actions are tracked with the actual email of who performed them.
                        </p>

                        {/* Domain Restriction Badge */}
                        <div className="flex items-center gap-2 mb-4 p-2 bg-blue/10 border border-blue/30 rounded-lg">
                            <Code size={16} className="text-blue flex-shrink-0" />
                            <span className="text-sm">
                                <strong>Hardcoded:</strong> Only <span className="font-mono bg-accent-light dark:bg-accent-dark px-1 rounded">@{LBA_ADMIN_EMAIL_DOMAIN}</span> emails allowed
                            </span>
                        </div>

                        {authorizedEmails.length === 0 ? (
                            <div className="text-center py-8">
                                <Mail size={32} className="mx-auto mb-3 text-muted-light dark:text-muted-dark" />
                                <p className="text-muted-light dark:text-muted-dark">
                                    No authorized emails yet. Add an email to get started.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {authorizedEmails.map((emailRecord) => (
                                    <div
                                        key={emailRecord._id}
                                        className={`flex items-center justify-between p-4 rounded-lg border ${emailRecord.isActive
                                            ? 'border-border-light dark:border-border-dark bg-accent-light dark:bg-accent-dark'
                                            : 'border-dashed border-muted-light dark:border-muted-dark opacity-60'
                                            }`}
                                    >
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium">{emailRecord.email}</p>
                                                {!emailRecord.isActive && (
                                                    <Badge variant="warning">Inactive</Badge>
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-light dark:text-muted-dark mt-1">
                                                Added {formatDate(emailRecord.createdAt)}
                                                {emailRecord.addedByAdmin && ` by ${emailRecord.addedByAdmin.email}`}
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleToggleEmail(emailRecord._id, emailRecord.isActive)}
                                                className={`p-2 rounded hover:bg-border-light dark:hover:bg-border-dark ${emailRecord.isActive ? 'text-green' : 'text-muted-light dark:text-muted-dark'
                                                    }`}
                                                title={emailRecord.isActive ? 'Deactivate' : 'Activate'}
                                            >
                                                <Power size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleRemoveEmail(emailRecord._id)}
                                                className="p-2 rounded hover:bg-border-light dark:hover:bg-border-dark text-red"
                                                title="Remove"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>

                    {/* Activity Logs */}
                    <Card title="Recent LBA Activity">
                        {activityLogs === undefined ? (
                            <LoadingSpinner />
                        ) : activityLogs.length === 0 ? (
                            <p className="text-center py-8 text-muted-light dark:text-muted-dark">
                                No activity yet
                            </p>
                        ) : (
                            <div className="space-y-3 max-h-96 overflow-y-auto">
                                {activityLogs.map((log) => (
                                    <div
                                        key={log._id}
                                        className="p-3 bg-accent-light dark:bg-accent-dark rounded-lg"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Badge variant={getActionTypeBadgeVariant(log.actionType)}>
                                                        {getActionTypeLabel(log.actionType)}
                                                    </Badge>
                                                    <span className="text-sm font-medium">{log.actorEmail}</span>
                                                </div>
                                                {log.metadata?.ticketSubject && (
                                                    <p className="text-sm text-muted-light dark:text-muted-dark">
                                                        Ticket: {log.metadata.ticketSubject}
                                                    </p>
                                                )}
                                                {log.metadata?.messageContent && (
                                                    <p className="text-sm text-muted-light dark:text-muted-dark truncate max-w-md">
                                                        "{log.metadata.messageContent}"
                                                    </p>
                                                )}
                                            </div>
                                            <span className="text-xs text-muted-light dark:text-muted-dark whitespace-nowrap">
                                                {formatDate(log.timestamp)}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>
                </div>

                {/* Stats Sidebar */}
                <div className="space-y-6">
                    <Card title="Activity Stats (30 days)">
                        {activityStats === undefined ? (
                            <LoadingSpinner />
                        ) : (
                            <div className="space-y-4">
                                <div>
                                    <p className="text-3xl font-bold">{activityStats.totalActions}</p>
                                    <p className="text-sm text-muted-light dark:text-muted-dark">Total Actions</p>
                                </div>

                                {Object.entries(activityStats.actionTypeCounts || {}).length > 0 && (
                                    <div>
                                        <p className="text-sm font-medium mb-2">By Action Type</p>
                                        <div className="space-y-1">
                                            {Object.entries(activityStats.actionTypeCounts).map(([type, count]) => (
                                                <div key={type} className="flex justify-between text-sm">
                                                    <span className="text-muted-light dark:text-muted-dark">
                                                        {getActionTypeLabel(type)}
                                                    </span>
                                                    <span className="font-medium">{count}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {Object.entries(activityStats.actorCounts || {}).length > 0 && (
                                    <div>
                                        <p className="text-sm font-medium mb-2">By Actor</p>
                                        <div className="space-y-1">
                                            {Object.entries(activityStats.actorCounts).map(([email, count]) => (
                                                <div key={email} className="flex justify-between text-sm">
                                                    <span className="text-muted-light dark:text-muted-dark truncate max-w-32">
                                                        {email}
                                                    </span>
                                                    <span className="font-medium">{count}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>

                    <Card title="How It Works">
                        <div className="space-y-3 text-sm text-muted-light dark:text-muted-dark">
                            <p>
                                <strong className="text-foreground-light dark:text-foreground-dark">1.</strong> Add @{LBA_ADMIN_EMAIL_DOMAIN} emails above
                            </p>
                            <p>
                                <strong className="text-foreground-light dark:text-foreground-dark">2.</strong> When those emails log in via Google on the app, they're automatically authenticated as the LBA super account
                            </p>
                            <p>
                                <strong className="text-foreground-light dark:text-foreground-dark">3.</strong> All actions (messages, tickets) are logged with the actual email for audit
                            </p>
                            <hr className="border-border-light dark:border-border-dark my-3" />
                            <p className="text-xs">
                                <strong className="text-amber-500">Note:</strong> Non-@{LBA_ADMIN_EMAIL_DOMAIN} users have zero latency impact — the check is skipped entirely for other domains.
                            </p>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Add Email Modal */}
            <Modal
                isOpen={showAddModal}
                onClose={() => {
                    setShowAddModal(false);
                    setNewEmail('');
                }}
                title="Add Authorized Email"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAddEmail}
                            disabled={!newEmail.trim() || !isValidLBADomain(newEmail)}
                        >
                            Add Email
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    {/* Domain Restriction Warning */}
                    <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <div className="flex items-start gap-2">
                            <AlertTriangle size={18} className="text-amber-500 mt-0.5 flex-shrink-0" />
                            <div className="text-sm">
                                <p className="font-medium text-amber-500 mb-1">
                                    Domain Restriction: @{LBA_ADMIN_EMAIL_DOMAIN} only
                                </p>
                                <p className="text-muted-light dark:text-muted-dark">
                                    Only emails with the <strong>@{LBA_ADMIN_EMAIL_DOMAIN}</strong> domain can be added.
                                    This is hardcoded for security.
                                </p>
                            </div>
                        </div>
                    </div>

                    <Input
                        label="Email Address"
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder={`admin@${LBA_ADMIN_EMAIL_DOMAIN}`}
                        required
                    />

                    {newEmail && !isValidLBADomain(newEmail) && (
                        <p className="text-sm text-red">
                            Email must be @{LBA_ADMIN_EMAIL_DOMAIN}
                        </p>
                    )}

                    {/* Hardcoded Notice */}
                    <div className="flex items-start gap-2 p-2 bg-accent-light dark:bg-accent-dark rounded text-xs text-muted-light dark:text-muted-dark">
                        <Code size={14} className="mt-0.5 flex-shrink-0" />
                        <span>
                            This restriction is hardcoded in both the admin panel and backend.
                            A code change would be required to use a different domain.
                        </span>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

